import { useState } from "react"
import {
  Edit,
  Eye,
  Download,
  FileImage,
  FileSpreadsheet,
  Loader2,
  X,
  ChevronDown,
  Ticket,
  Plus,
  Trash2,
  Pencil,
  QrCode,
  ExternalLink,
  Users,
  FileText,
} from "lucide-react"
import axios from "axios"
import { useEffect } from "react"
import QrScanner from 'react-qr-scanner'

const DownloadButtons = ({
  loadingZip,
  loadingZipNoBg,
  loadingExcel,
  downloadAllImagesAsZip,
  downloadAllImagesWithoutBackgroundAsZip,
  downloadAllEntries,
  toggleModalOpenedit,
  toggleModalOpen,
  eventData,
  downloadAllCustomPDFVertical,
  participants,
  downloadAllCustomPNGVertical
}) => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false)
  const [isQRScannerModalOpen, setIsQRScannerModalOpen] = useState(false)
  const [coupons, setCoupons] = useState(eventData?.coupons || [])
  const [editingCoupon, setEditingCoupon] = useState(null)
  const [newCoupon, setNewCoupon] = useState({ name: "", type: "fixed", value: 0, scope: "indian" })
  const [couponUsageMap, setCouponUsageMap] = useState({});
  const [scannedData, setScannedData] = useState("")
  const [isScanning, setIsScanning] = useState(false)
  const [scanHistory, setScanHistory] = useState([])
  const [scanDelay, setScanDelay] = useState(300) // Scan delay in milliseconds

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const formData = new FormData()
      const cleanedCoupons = coupons
        .filter((c) => c && c.name && c.type && c.value)
        .map((c) => ({ ...c, value: Number(c.value) }))
      formData.append("regionPricings", JSON.stringify(eventData?.regionPricings))
      formData.append("coupons", JSON.stringify(cleanedCoupons))
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/events/edit/${eventData._id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      )
      setIsCouponModalOpen(false)
    } catch (error) {
      console.error("Error saving coupons:", error)
      alert("Error saving coupons")
    }
  }

  function getCouponUsageMap() {
    const couponUsageMap = {};
    participants.forEach(p => {
      const code = p.useCouponCode;
      if (code) {
        couponUsageMap[code] = (couponUsageMap[code] || 0) + 1;
      }
    });
    return couponUsageMap;
  }

  useEffect(() => {
    setCouponUsageMap(getCouponUsageMap());
  }, [participants])

  // QR Scanner functions
  const handleQRScan = (data) => {
    if (data) {
      setScannedData(data);
      
      const newScan = {
        id: Date.now(),
        data: data,
        timestamp: new Date().toLocaleString(),
        isUrl: isValidUrl(data)
      };
      setScanHistory(prev => [newScan, ...prev.slice(0, 9)]);

      // If it's a URL, navigate to it
      if (isValidUrl(data)) {
        const shouldNavigate = window.confirm(`QR Code contains a link:\n\n${data}\n\nDo you want to open it?`);
        if (shouldNavigate) {
          window.open(data, '_blank', 'noopener,noreferrer');
        }
      }

      // Stop scanning after successful scan
      setIsScanning(false);
    }
  };

  const handleQRError = (err) => {
    console.error('QR Scan Error:', err);
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleQRScannerClose = () => {
    setIsScanning(false);
    setIsQRScannerModalOpen(false);
    setScannedData("");
  };

  const startScanning = () => {
    setIsScanning(true);
    setScannedData("");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6 relative">
      {/* Professional background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 rounded-2xl opacity-40"></div>

      <div className="relative flex flex-wrap gap-4 justify-center">
        {/* Edit All ID Cards Button */}
        <button
          className="group h-13 relative overflow-hidden bg-gradient-to-r from-slate-700 via-slate-800 to-gray-800 hover:from-slate-600 hover:via-slate-700 hover:to-gray-700 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-slate-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-slate-600/30 hover:border-slate-500/50"
          onClick={toggleModalOpenedit}
        >
          <div className="flex items-center justify-center space-x-2 relative z-10">
            <Edit className="w-5 h-5 group-hover:rotate-6 transition-transform duration-300" />
            <span className="text-sm font-medium">Edit All ID Cards</span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
        </button>

        {/* Show & Hide Button */}
        <button
          className="group relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-500 hover:via-blue-600 hover:to-indigo-600 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-blue-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-blue-500/30 hover:border-blue-400/50"
          onClick={toggleModalOpen}
        >
          <div className="flex items-center justify-center space-x-2 relative z-10">
            <Eye className="w-5 h-5 group-hover:scale-105 transition-transform duration-300" />
            <span className="text-sm font-medium">Show & Hide</span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
        </button>

        {/* QR Code Scanner Button */}
        {/* <button
          className="group relative overflow-hidden bg-gradient-to-r from-orange-600 via-amber-700 to-yellow-700 hover:from-orange-500 hover:via-amber-600 hover:to-yellow-600 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-orange-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-orange-500/30 hover:border-orange-400/50"
          onClick={() => setIsQRScannerModalOpen(true)}
        >
          <div className="flex items-center justify-center space-x-2 relative z-10">
            <QrCode className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
            <span className="text-sm font-medium">QR Scanner</span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
        </button> */}

        {/* Download Options Button */}
        <button
          className="group relative overflow-hidden bg-gradient-to-r from-emerald-600 via-green-700 to-teal-700 hover:from-emerald-500 hover:via-green-600 hover:to-teal-600 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-emerald-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-emerald-500/30 hover:border-emerald-400/50"
          onClick={() => setIsDownloadModalOpen(true)}
        >
          <div className="flex items-center justify-center space-x-2 relative z-10">
            <Download className="w-5 h-5 group-hover:translate-y-0.5 transition-transform duration-300" />
            <span className="text-sm font-medium">Download Options</span>
            <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-300" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
        </button>

        <button
  className="group relative overflow-hidden bg-gradient-to-r from-cyan-600 via-blue-700 to-indigo-700 hover:from-cyan-500 hover:via-blue-600 hover:to-indigo-600 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-cyan-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-cyan-500/30 hover:border-cyan-400/50"
  onClick={() => window.open(`/full-participant-details/${eventData._id}`, '_blank')}
>
  <div className="flex items-center justify-center space-x-2 relative z-10">
    <Users className="w-5 h-5 group-hover:scale-105 transition-transform duration-300" />
    <span className="text-sm font-medium">Full Participant Details</span>
  </div>
  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
</button>

        {/* Coupon Code Button */}
        {eventData?.isPaidEvent && (
          <button
            className="group relative overflow-hidden bg-gradient-to-r from-purple-600 via-pink-700 to-rose-700 hover:from-purple-500 hover:via-pink-600 hover:to-rose-600 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-purple-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-purple-500/30 hover:border-purple-400/50"
            onClick={() => setIsCouponModalOpen(true)}
          >
            <div className="flex items-center justify-center space-x-2 relative z-10">
              <Ticket className="w-5 h-5 group-hover:rotate-6 transition-transform duration-300" />
              <span className="text-sm font-medium">Coupon Code</span>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
          </button>
        )}
      </div>

      <button
  onClick={downloadAllCustomPNGVertical}
  disabled={loadingZip}
  className="group relative overflow-hidden bg-gradient-to-r from-purple-600 via-violet-700 to-purple-800 hover:from-purple-500 hover:via-violet-600 hover:to-purple-700 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-purple-500/30 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-purple-500/40 hover:border-purple-400/60 min-w-[200px]"
>
  <div className="flex items-center justify-center space-x-3 relative z-10">
    {loadingZip ? (
      <Loader2 className="w-5 h-5 animate-spin" />
    ) : (
      <Download className="w-5 h-5" />
    )}
    <span className="text-sm font-medium">
      {loadingZip ? 'Generating...' : 'Bulk PNG Vertical'}
    </span>
  </div>
  
  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
</button>


<button
  onClick={downloadAllCustomPDFVertical}
  disabled={loadingZip}
  className="group relative overflow-hidden bg-gradient-to-r from-green-600 via-emerald-700 to-green-800 hover:from-green-500 hover:via-emerald-600 hover:to-green-700 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-green-500/30 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-green-500/40 hover:border-green-400/60 min-w-[200px]"
>
  <div className="flex items-center justify-center space-x-3 relative z-10">
    {loadingZip ? (
      <Loader2 className="w-5 h-5 animate-spin" />
    ) : (
      <FileText className="w-5 h-5" />
    )}
    <span className="text-sm font-medium">
      {loadingZip ? 'Generating...' : 'Bulk PDF Vertical'}
    </span>
  </div>
</button>


      {/* Professional subtle accents */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
        <div className="absolute top-6 left-6 w-1 h-1 bg-blue-400 rounded-full opacity-20 animate-pulse delay-0"></div>
        <div className="absolute top-4 right-8 w-0.5 h-0.5 bg-slate-400 rounded-full opacity-30 animate-pulse delay-1000"></div>
        <div className="absolute bottom-8 left-16 w-1 h-1 bg-emerald-400 rounded-full opacity-15 animate-pulse delay-2000"></div>
        <div className="absolute bottom-6 right-12 w-0.5 h-0.5 bg-indigo-400 rounded-full opacity-25 animate-pulse delay-3000"></div>
      </div>

      {/* Professional grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(51 65 85) 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* QR Scanner Modal */}
      {isQRScannerModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-orange-600" />
                  QR Code Scanner
                  {scannedData && isValidUrl(scannedData) && (
                    <span className="flex items-center gap-1 text-sm bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      <ExternalLink className="w-3 h-3" />
                      Link Detected
                    </span>
                  )}
                </h2>
                <button
                  onClick={handleQRScannerClose}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Scanner Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-800">Camera Scanner</h3>
                    {isScanning && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Scan Speed:</label>
                        <select
                          value={scanDelay}
                          onChange={(e) => setScanDelay(Number(e.target.value))}
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                        >
                          <option value={100}>Fast (100ms)</option>
                          <option value={300}>Normal (300ms)</option>
                          <option value={500}>Slow (500ms)</option>
                        </select>
                      </div>
                    )}
                  </div>
                  
                  {!isScanning && !scannedData && (
                    <div className="text-center space-y-4">
                      <div className="w-32 h-32 mx-auto bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl flex items-center justify-center">
                        <QrCode className="w-16 h-16 text-orange-600" />
                      </div>
                      <p className="text-gray-600">Click the button below to start scanning QR codes</p>
                      <button
                        onClick={startScanning}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                      >
                        Start Scanning
                      </button>
                    </div>
                  )}

                  {isScanning && (
                    <div className="space-y-4">
                      <div className="relative rounded-lg overflow-hidden bg-black">
                        <QrScanner
                          delay={scanDelay}
                          onError={handleQRError}
                          onScan={handleQRScan}
                          style={{ 
                            width: '100%', 
                            height: '300px',
                            objectFit: 'cover'
                          }}
                          facingMode="environment" // Use back camera
                        />
                        {/* Scanning overlay */}
                        <div className="absolute inset-4 border-2 border-orange-500 rounded-lg pointer-events-none">
                          <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-orange-500 rounded-tl-lg"></div>
                          <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-orange-500 rounded-tr-lg"></div>
                          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-orange-500 rounded-bl-lg"></div>
                          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-orange-500 rounded-br-lg"></div>
                        </div>
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                          Position QR code within frame
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <button
                          onClick={() => setIsScanning(false)}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200"
                        >
                          Stop Scanning
                        </button>
                      </div>
                    </div>
                  )}

                  {scannedData && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-lg font-medium text-green-800">Latest Scan Result:</h4>
                          {isValidUrl(scannedData) && (
                            <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                              <ExternalLink className="w-3 h-3" />
                              URL
                            </span>
                          )}
                        </div>
                        <div className="bg-white p-3 rounded border text-sm text-gray-700 break-all max-h-32 overflow-y-auto">
                          {scannedData}
                        </div>
                      </div>
                      <div className="flex gap-2 justify-center flex-wrap">
                        <button
                          onClick={startScanning}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200"
                        >
                          Scan Another
                        </button>
                        <button
                          onClick={() => copyToClipboard(scannedData)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200"
                        >
                          Copy Text
                        </button>
                        {isValidUrl(scannedData) && (
                          <button
                            onClick={() => window.open(scannedData, '_blank', 'noopener,noreferrer')}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200 flex items-center gap-1"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open Link
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Scan History Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800">Scan History</h3>
                  {scanHistory.length === 0 ? (
                    <div className="text-center text-gray-500 text-sm py-8 border-2 border-dashed border-gray-200 rounded-lg">
                      No scans yet
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {scanHistory.map((scan) => (
                        <div
                          key={scan.id}
                          className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow duration-200"
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className="text-xs text-gray-500">{scan.timestamp}</span>
                            {scan.isUrl && (
                              <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                <ExternalLink className="w-3 h-3" />
                                URL
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-700 break-all bg-white p-2 rounded border max-h-20 overflow-y-auto">
                            {scan.data}
                          </div>
                          <div className="flex gap-1 mt-2">
                            <button
                              onClick={() => copyToClipboard(scan.data)}
                              className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors duration-200"
                            >
                              Copy
                            </button>
                            {scan.isUrl && (
                              <button
                                onClick={() => window.open(scan.data, '_blank', 'noopener,noreferrer')}
                                className="text-xs bg-blue-200 hover:bg-blue-300 text-blue-700 px-2 py-1 rounded transition-colors duration-200 flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Open
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center">
                {isScanning ? 'Camera is active - point at a QR code to scan' : 'QR Scanner ready to use • Links will be opened automatically after confirmation'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Download Options Modal */}
      {isDownloadModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Download className="w-5 h-5 text-emerald-600" />
                  Download Options
                </h2>
                <button
                  onClick={() => setIsDownloadModalOpen(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <button
                className="w-full group relative overflow-hidden bg-gradient-to-r from-emerald-600 via-green-700 to-teal-700 hover:from-emerald-500 hover:via-green-600 hover:to-teal-600 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-emerald-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-emerald-500/30 hover:border-emerald-400/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
                onClick={downloadAllImagesAsZip}
                disabled={loadingZip}
              >
                <div className="flex items-center justify-center space-x-3 relative z-10">
                  {loadingZip ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm font-medium">Processing...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 group-hover:translate-y-0.5 transition-transform duration-300" />
                      <span className="text-sm font-medium">Download All Images as ZIP</span>
                    </>
                  )}
                </div>
                {!loadingZip && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                )}
              </button>

              <button
                className="w-full group relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-700 to-violet-700 hover:from-indigo-500 hover:via-purple-600 hover:to-violet-600 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-indigo-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-indigo-500/30 hover:border-indigo-400/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
                onClick={downloadAllImagesWithoutBackgroundAsZip}
                disabled={loadingZipNoBg}
              >
                <div className="flex items-center justify-center space-x-3 relative z-10">
                  {loadingZipNoBg ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm font-medium">Processing...</span>
                    </>
                  ) : (
                    <>
                      <FileImage className="w-5 h-5 group-hover:scale-105 transition-transform duration-300" />
                      <span className="text-sm font-medium">Download Images Without Background as ZIP</span>
                    </>
                  )}
                </div>
                {!loadingZipNoBg && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                )}
              </button>

              <button
                className="w-full group relative overflow-hidden bg-gradient-to-r from-amber-600 via-orange-700 to-red-700 hover:from-amber-500 hover:via-orange-600 hover:to-red-600 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-amber-500/20 hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 ease-out border border-amber-500/30 hover:border-amber-400/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
                onClick={downloadAllEntries}
                disabled={loadingExcel}
              >
                <div className="flex items-center justify-center space-x-3 relative z-10">
                  {loadingExcel ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm font-medium">Preparing Excel...</span>
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-5 h-5 group-hover:translate-y-0.5 transition-transform duration-300" />
                      <span className="text-sm font-medium">Download All Entries as Excel</span>
                    </>
                  )}
                </div>
                {!loadingExcel && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                )}
              </button>
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center">
                Choose your preferred download format from the options above
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Code Modal */}
      {isCouponModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-purple-600" />
                  Coupon Codes
                </h2>
                <button
                  onClick={() => setIsCouponModalOpen(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  Add New Coupon
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <input
                    type="text"
                    placeholder="Coupon Name"
                    value={newCoupon.name.toUpperCase()}
                    onChange={(e) => setNewCoupon({ ...newCoupon, name: e.target.value.toUpperCase() })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <select
                    value={newCoupon.type}
                    onChange={(e) => setNewCoupon({ ...newCoupon, type: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="fixed">Fixed Amount</option>
                    <option value="percentage">Percentage</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Value"
                    value={newCoupon.value}
                    onChange={(e) => setNewCoupon({ ...newCoupon, value: Number.parseInt(e.target.value) })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <select
                    value={newCoupon.scope}
                    onChange={(e) => setNewCoupon({ ...newCoupon, scope: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="indian">Indian</option>
                    <option value="international">International</option>
                  </select>
                  <button
                    onClick={() => {
                      if (newCoupon.name && newCoupon.value) {
                        setCoupons([...coupons, newCoupon])
                        setNewCoupon({ name: "", type: "fixed", value: 0, scope: "indian" })
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    Add Coupon
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Indian Coupons */}
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    Indian Coupons
                  </h4>
                  <div className="space-y-3">
                    {coupons
                      .filter((coupon) => coupon.scope === "indian")
                      .map((coupon, index) => {
                        const originalIndex = coupons.findIndex(
                          (c) => c.name === coupon.name && c.scope === coupon.scope,
                        )
                        return (
                          <div
                            key={`indian-${index}`}
                            className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-md transition-shadow duration-200"
                          >
                            {editingCoupon === originalIndex ? (
                              <div className="flex items-center gap-4 flex-1">
                                <input
                                  type="text"
                                  value={coupon.name}
                                  onChange={(e) => {
                                    const updated = [...coupons]
                                    updated[originalIndex].name = e.target.value
                                    setCoupons(updated)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <select
                                  value={coupon.type}
                                  onChange={(e) => {
                                    const updated = [...coupons]
                                    updated[originalIndex].type = e.target.value
                                    setCoupons(updated)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="fixed">Fixed Amount</option>
                                  <option value="percentage">Percentage</option>
                                </select>
                                <input
                                  type="number"
                                  value={coupon.value}
                                  onChange={(e) => {
                                    const updated = [...coupons]
                                    updated[originalIndex].value = Number.parseInt(e.target.value)
                                    setCoupons(updated)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <select
                                  value={coupon.scope}
                                  onChange={(e) => {
                                    const updated = [...coupons]
                                    updated[originalIndex].scope = e.target.value
                                    setCoupons(updated)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="indian">Indian</option>
                                  <option value="international">International</option>
                                </select>
                                <button
                                  onClick={() => setEditingCoupon(null)}
                                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors duration-200"
                                >
                                  Save
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-4">
                                  <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-mono font-semibold">
                                    {coupon.name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {coupon.scope === "international"
                                      ? `$${coupon.value}`
                                      : coupon.type === "fixed"
                                        ? `₹${coupon.value}`
                                        : `${coupon.value}%`}{" "}
                                    off
                                  </div>
                                  <div className="text-xs text-gray-500 capitalize bg-gray-100 px-2 py-1 rounded">
                                    {coupon.type}
                                  </div>
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                    Indian
                                  </span>
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                    {couponUsageMap[coupon.name] || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      const updated = coupons.filter((_, i) => i !== originalIndex)
                                      setCoupons(updated)
                                    }}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )
                      })}
                    {coupons.filter((coupon) => coupon.scope === "indian").length === 0 && (
                      <div className="text-center text-gray-500 text-sm py-8 border-2 border-dashed border-gray-200 rounded-lg">
                        No Indian coupon codes added yet
                      </div>
                    )}
                  </div>
                </div>

                {/* International Coupons */}
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    International Coupons
                  </h4>
                  <div className="space-y-3">
                    {coupons
                      .filter((coupon) => coupon.scope === "international")
                      .map((coupon, index) => {
                        const originalIndex = coupons.findIndex(
                          (c) => c.name === coupon.name && c.scope === coupon.scope,
                        )
                        return (
                          <div
                            key={`international-${index}`}
                            className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-md transition-shadow duration-200"
                          >
                            {editingCoupon === originalIndex ? (
                              <div className="flex items-center gap-4 flex-1">
                                <input
                                  type="text"
                                  value={coupon.name}
                                  onChange={(e) => {
                                    const updated = [...coupons]
                                    updated[originalIndex].name = e.target.value
                                    setCoupons(updated)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <select
                                  value={coupon.type}
                                  onChange={(e) => {
                                    const updated = [...coupons]
                                    updated[originalIndex].type = e.target.value
                                    setCoupons(updated)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="fixed">Fixed Amount</option>
                                  <option value="percentage">Percentage</option>
                                </select>
                                <input
                                  type="number"
                                  value={coupon.value}
                                  onChange={(e) => {
                                    const updated = [...coupons]
                                    updated[originalIndex].value = Number.parseInt(e.target.value)
                                    setCoupons(updated)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <select
                                  value={coupon.scope}
                                  onChange={(e) => {
                                    const updated = [...coupons]
                                    updated[originalIndex].scope = e.target.value
                                    setCoupons(updated)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="indian">Indian</option>
                                  <option value="international">International</option>
                                </select>
                                <button
                                  onClick={() => setEditingCoupon(null)}
                                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors duration-200"
                                >
                                  Save
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-4">
                                  <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-mono font-semibold">
                                    {coupon.name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {coupon.scope === "indian"
                                      ? `₹${coupon.value}`
                                      : coupon.type === "fixed"
                                        ? `$${coupon.value}`
                                        : `${coupon.value}%`}{" "}
                                    off
                                  </div>
                                  <div className="text-xs text-gray-500 capitalize bg-gray-100 px-2 py-1 rounded">
                                    {coupon.type}
                                  </div>
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                    International
                                  </span>
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                    {couponUsageMap[coupon.name] || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      const updated = coupons.filter((_, i) => i !== originalIndex)
                                      setCoupons(updated)
                                    }}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )
                      })}
                    {coupons.filter((coupon) => coupon.scope === "international").length === 0 && (
                      <div className="text-center text-gray-500 text-sm py-8 border-2 border-dashed border-gray-200 rounded-lg">
                        No International coupon codes added yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Total Coupons: {coupons.length}
                <span className="ml-4">Indian: {coupons.filter((c) => c.scope === "indian").length}</span>
                <span className="ml-2">International: {coupons.filter((c) => c.scope === "international").length}</span>
              </p>
              <button
                onClick={handleSubmit}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors duration-200 font-medium"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DownloadButtons
